import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ApiUrls } from "./ApiUrls";
import { Page } from "../model/Page";
import { ProApproverListModel } from "../model/ProApproverListModel";
import { ProApproverDetailModel } from "../model/ProApproverDetailModel";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileResponseModel } from "../model/FileResponseModel";
import { ApplicantDetailListModel } from "../model/ApplicantDetailListModel";
import { serverUrl } from "../serverUrl";
import { catchError, map } from "rxjs/operators";
import { Observable, of, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ProApproverService {
  constructor(private httpClient: HttpClient, private _snackBar: MatSnackBar) {}

  fetchProApproverList(
    page: number,
    size: number,
    filterText: string,
    verificationStatus: string
  ) {
    return this.httpClient.get<Page<ProApproverListModel>>(
      ApiUrls.proApproverList +
        `?page=${page}&size=${size}&filterText=${filterText}&verificationStatus=${verificationStatus}`
    );
  }

  fetchProApproverDetail(certJournalistKey: number) {
    return this.httpClient.get<ProApproverDetailModel>(
      ApiUrls.proApproverDetail.replace(
        "{certJournalistKey}",
        certJournalistKey.toString()
      )
    );
  }

  fetchEsignCertificate(certJournalistKey: number) {
    return this.httpClient.get<{
      status: string;
      errorMessage: string;
      data: string | ArrayBuffer;
    }>(
      ApiUrls.fetchEsignReceipt.replace(
        "{certJournalistKey}",
        certJournalistKey.toString()
      )
    );
  }

  submitVerificationDetail(
    certJournalistKey: number,
    verificationStatus: string,
    rejectReason: string,
    outwardNo: string
  ) {
    return this.httpClient.post<{
      status: string;
      esignKey: number;
      url: string;
      errorMessage: string;
    }>(
      ApiUrls.proApproverDetail.replace(
        "{certJournalistKey}",
        certJournalistKey.toString()
      ),
      {
        verificationStatus: verificationStatus,
        outwardNo: outwardNo,
        rejectReason: rejectReason,
      }
    );
  }

  fetchApplicantApprovePendingList(
    page: number,
    size: number,
    filterText: string,
    verificationStatus: string
  ) {
    return this.httpClient.get<Page<ApplicantDetailListModel>>(
      ApiUrls.fetchApplicantApprovePendingList +
        `?page=${page}&size=${size}&filterText=${filterText}&verificationStatus=${verificationStatus}`
    );
  }

  fetchApplicantPhotoDetailBykey(key: number) {
    return this.httpClient.get<ApplicantDetailListModel>(
      ApiUrls.fetchApplicantPhotoDetailBykey.replace("{key}", key.toString())
    );
  }

  fetchApplicantMarriageStatusDetailBykey(key: number) {
    return this.httpClient.get<ApplicantDetailListModel>(
      ApiUrls.fetchApplicantMarriageStatusDetailBykey.replace(
        "{key}",
        key.toString()
      )
    );
  }

  submitVerificationDetailForApplicant(
    key: number,
    statusType: string,
    rejectReason: string
  ) {
    return this.httpClient.post<{
      status: string;
      url: string;
      errorMessage: string;
    }>(ApiUrls.approveDetailForApplicant.replace("{key}", key.toString()), {
      statusType: statusType,
      rejectReason: rejectReason,
    });
  }

  updateDetailsInApplicantTable(
    applicationNo: number,
    newApplicantPhoto: string,
    selectedFileName: string
  ) {
    return this.httpClient.post<{
      status: string;
      url: string;
      errorMessage: string;
    }>(
      ApiUrls.updateDetailsInApplicantTable.replace(
        "{applicationNo}",
        applicationNo.toString()
      ),
      {
        applicationNo: applicationNo,
        newApplicantPhoto: newApplicantPhoto,
        selectedFileName: selectedFileName,
      }
    );
  }

  updateMarriageDetailsInApplicantTable(
    applicationNo: number,
    newMaritalStatus: string,
    spouseName: string,
    fileNameAaadhar: string,
    fileNameMarriageCertificate: string,
    fileNamePan: string,
    fileNamePanWithExtension: string,
    fileNameAaadharWithExtension: string,
    fileNameMarriageCertificateWithExtension: string
  ) {
    return this.httpClient.post<{
      status: string;
      url: string;
      errorMessage: string;
    }>(
      ApiUrls.updateMarriageDetailsInApplicantTable.replace(
        "{applicationNo}",
        applicationNo.toString()
      ),
      {
        applicationNo: applicationNo,
        newMaritalStatus: newMaritalStatus,
        spouseName: spouseName,
        fileNameAaadhar: fileNameAaadhar,
        fileNameMarriageCertificate: fileNameMarriageCertificate,
        fileNamePan: fileNamePan,
        fileNamePanWithExtension: fileNamePanWithExtension,
        fileNameAaadharWithExtension: fileNameAaadharWithExtension,
        fileNameMarriageCertificateWithExtension:
          fileNameMarriageCertificateWithExtension,
      }
    );
  }

  downloadFile(documentPath: any): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append("documentPath", documentPath);
    return this.httpClient
      .post<any>(serverUrl + "open/downloadDocFile", queryParams, {
        responseType: "arraybuffer" as "json",
        headers: new HttpHeaders().set(
          "Content-Type",
          "application/x-www-form-urlencoded;charset=utf-8"
        ),
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((err) => {
          return throwError(err);
        }),
        catchError((err) => {
          return of(err);
        })
      );
  }

  async successToast(msg) {
    await this._snackBar.open(msg, "Close", {
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: "success-toast",
      duration: 3 * 1000,
    });
  }

  async errorToast(errorMessage: string, dur?: number) {
    await this._snackBar.open(errorMessage, "Close", {
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: "error-toast",
      duration: dur ? dur : 3 * 1000,
    });
  }
}
